import Link from 'src/components/Link'

import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import TuneIcon from '@mui/icons-material/Tune'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import { Theme } from '@mui/material/styles'

import ActiveFilters from '../ActiveFilters/ActiveFilters'
import { hrefForNavigationItem } from 'src/functions/navigationBreadcrumbs'
import { useSelector } from 'src/redux/hooks'
import type { item as NavigationItem } from 'src/shared/frontendTypes/navigation'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'grid',
      gap: theme.spacing(1.75),
      margin: theme.spacing(3, 0),
    },
    subcategoryNav: {
      display: 'flex',
      gap: theme.spacing(1.25),
      overflowX: 'auto',
      overscrollBehaviorX: 'contain',
      paddingBottom: theme.spacing(.5),
      scrollbarWidth: 'none',
      whiteSpace: 'nowrap',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
      [theme.breakpoints.down('sm')]: {
        marginInline: theme.spacing(-2),
        paddingInline: theme.spacing(2),
      },
    },
    subcategoryBubble: {
      alignItems: 'center',
      backgroundColor: 'var(--page-builder-surface, #ffffff)',
      border: '1px solid rgba(15, 23, 42, 0.14)',
      borderRadius: 999,
      color: 'var(--page-builder-text, #1f2933)',
      display: 'inline-flex',
      fontSize: '.88rem',
      fontWeight: 700,
      minHeight: 42,
      padding: theme.spacing(0, 2.2),
      textDecoration: 'none',
      transition: 'border-color .18s ease, background-color .18s ease, color .18s ease',
      '&:hover': {
        backgroundColor: 'var(--page-builder-text, #1f2933)',
        borderColor: 'var(--page-builder-text, #1f2933)',
        color: 'var(--page-builder-surface, #ffffff)',
      },
      [theme.breakpoints.down('sm')]: {
        minHeight: 38,
        paddingInline: theme.spacing(1.8),
      },
    },
    filterRow: {
      alignItems: 'center',
      display: 'flex',
      flexWrap: 'nowrap',
      gap: theme.spacing(1.25),
      justifyContent: 'space-between',
      minHeight: 42,
      padding: 0,
      [theme.breakpoints.down('sm')]: {
        gap: theme.spacing(.8),
        minHeight: 38,
      },
    },
    filterAction: {
      alignItems: 'center',
      display: 'flex',
      flex: '1 1 auto',
      minWidth: 0,
    },
    activeFiltersRow: {
      minWidth: 0,
      overflow: 'hidden',
      overflowX: 'auto',
      scrollbarWidth: 'none',
      whiteSpace: 'nowrap',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
      '& > div': {
        maxWidth: '100%',
      },
      [theme.breakpoints.down('sm')]: {
        marginInline: theme.spacing(-2),
        paddingInline: theme.spacing(2),
      },
    },
    metaWrap: {
      alignItems: 'center',
      flex: '0 0 auto',
      display: 'flex',
      gap: theme.spacing(1),
      marginLeft: 'auto',
      [theme.breakpoints.down('sm')]: {
        gap: theme.spacing(.7),
      },
    },
    count: {
      fontSize: '.84rem',
      fontWeight: 700,
      letterSpacing: '.04em',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      [theme.breakpoints.down('sm')]: {
        fontSize: '.76rem',
      },
    },
    mobileFilterButton: {
      borderColor: 'rgba(15, 23, 42, 0.18) !important',
      color: 'var(--page-builder-text, #1f2933) !important',
      minWidth: '0 !important',
      whiteSpace: 'nowrap',
      [theme.breakpoints.down('sm')]: {
        fontSize: '.76rem !important',
        minHeight: '36px !important',
        padding: theme.spacing(0, 1.2),
        '& .MuiButton-startIcon': {
          marginLeft: 0,
          marginRight: theme.spacing(.5),
        },
      },
      [theme.breakpoints.up('md')]: {
        display: 'none !important',
      },
    },
  })
)

interface Props {
  subcategoryItems?: NavigationItem[]
  subcategoryParentBrand?: NavigationItem | null
  count?: number
  showFilterButton?: boolean
  onFilterClick?: () => void
}

const getActiveFilterCount = (
  suche: string,
  activeBrands: Array<string | number>,
  activeFilters: { [filterId: number]: number | number[] }
) => {
  const optionCount = Object.values(activeFilters).reduce<number>((sum, value) => {
    if (Array.isArray(value)) return sum + value.length
    return sum + 1
  }, 0)

  return (suche.length >= 3 ? 1 : 0) + activeBrands.length + optionCount
}

const FilterTopBar: React.FC<Props> = ({
  subcategoryItems = [],
  subcategoryParentBrand = null,
  count,
  showFilterButton,
  onFilterClick,
}) => {
  const classes = useStyles()
  const { suche, activeBrands, activeFilters } = useSelector(store => store.Filter)
  const activeFilterCount = getActiveFilterCount(suche, activeBrands, activeFilters)
  const subcategories = subcategoryItems
    .map(item => ({
      item,
      href: hrefForNavigationItem(item, subcategoryParentBrand),
      title: String(item.title || '').trim(),
    }))
    .filter(entry => entry.title && entry.href)

  const hasTopRow = showFilterButton || typeof count === 'number'
  const hasFilterRow = hasTopRow || activeFilterCount > 0

  if (subcategories.length === 0 && !hasFilterRow) return null

  return <div className={classes.root}>
    {subcategories.length > 0 &&
      <nav className={classes.subcategoryNav} aria-label='Unterkategorien'>
        {subcategories.map(({ item, href, title }) =>
          <Link key={`${item.id}-${item.menueId || item.brand || href}`} href={href || '#'} passHref legacyBehavior>
            <a className={classes.subcategoryBubble}>
              {title}
            </a>
          </Link>
        )}
      </nav>
    }

    {hasTopRow &&
      <div className={classes.filterRow}>
        <div className={classes.filterAction}>
          {showFilterButton &&
            <Button
              variant='outlined'
              startIcon={<TuneIcon />}
              className={classes.mobileFilterButton}
              onClick={onFilterClick}
            >
              Filter
            </Button>
          }
        </div>

        <div className={classes.metaWrap}>
          {typeof count === 'number' &&
            <Typography component='p' className={classes.count}>
              {count} Produkte
            </Typography>
          }

        </div>
      </div>
    }

    {activeFilterCount > 0 &&
      <div className={classes.activeFiltersRow}>
        <ActiveFilters />
      </div>
    }
  </div>
}

export default FilterTopBar
